import React, { useState } from "react";
import { ChevronDown } from "react-feather";
import DialCodePickerModal from "../../DialCodePicker";
import {
  DialCode,
  FALLBACK_DIAL_CODE,
  countryNameFor,
  dialCodeForCallingCode,
  dialCodeForCountry,
} from "../../../utils/dialCode";
import {
  draftPhone,
  formatNational,
  phoneToSubmit,
  splitE164,
} from "../../../utils/phone";
import styles from "./PhoneInput.module.scss";

export type { DialCode };

export interface PhoneInputProps {
  /**
   * Receives the canonical E.164 number, or "" while what's typed couldn't be
   * a real number — so callers can gate submission on a truthy value.
   */
  onChange: (full: string) => void;
  disabled?: boolean;
  onEnter?: () => void;
  /**
   * Dial code to start on — derive it with `resolveDefaultDialCode(location
   * country)` so a customer at an Australian venue doesn't start on +27.
   * Defaults to South Africa where there is no location to go on. May arrive
   * after mount (settings load async); it is adopted while the number field is
   * still empty, so it can never clobber what the user typed or a flag they
   * picked themselves.
   */
  defaultDialCode?: DialCode;
  /** Full E.164 number to prefill, e.g. carried over from an earlier step. */
  defaultValue?: string;
  /**
   * Validation message from the surrounding form. Rendered only when this
   * field has nothing more specific to say, so a half-typed number shows
   * "not a full number" rather than that plus a generic "required".
   */
  error?: string;
}

/** The picker entry a parsed number belongs to, for prefills and pastes. */
const dialCodeForParsed = (
  parsed: NonNullable<ReturnType<typeof splitE164>>,
): DialCode | null =>
  dialCodeForCountry(parsed.country) ??
  dialCodeForCallingCode(parsed.callingCode);

const PhoneInput: React.FC<PhoneInputProps> = ({
  onChange,
  disabled = false,
  onEnter,
  defaultDialCode = FALLBACK_DIAL_CODE,
  defaultValue,
  error,
}) => {
  const initial = () => {
    const parsed = splitE164(defaultValue);
    const dial = parsed && dialCodeForParsed(parsed);
    return dial ? { dial, national: parsed!.national } : null;
  };

  const [dialCode, setDialCode] = useState<DialCode>(
    () => initial()?.dial ?? defaultDialCode,
  );
  // The digits exactly as typed. Rendered raw while focused and formatted once
  // the field is left, so there is no caret to fight with mid-edit.
  const [national, setNational] = useState(() => initial()?.national ?? "");
  const [focused, setFocused] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Adopt a default that resolved after mount, but only while the field is
  // untouched — see `defaultDialCode` above.
  React.useEffect(() => {
    if (!national) setDialCode(defaultDialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDialCode.dial_code]);

  const draft = draftPhone(national, dialCode.code);

  const emit = (value: string, dial: DialCode) =>
    onChange(
      phoneToSubmit(draftPhone(value, dial.code), `${dial.dial_code}${value}`),
    );

  const handleLocalChange = (raw: string) => {
    // A pasted "+<cc>…" identifies its own country — follow it instead of
    // treating the country code as part of the local number.
    const pasted = raw.trim().startsWith("+") ? splitE164(raw.trim()) : null;
    const pastedDial = pasted && dialCodeForParsed(pasted);
    if (pasted && pastedDial) {
      setDialCode(pastedDial);
      setNational(pasted.national);
      emit(pasted.national, pastedDial);
      return;
    }
    const digits = raw.replace(/\D/g, "");
    setNational(digits);
    emit(digits, dialCode);
  };

  const handleDialCodeChange = (dc: DialCode) => {
    setDialCode(dc);
    setPickerOpen(false);
    emit(national, dc);
  };

  // Only once they've left the field — nagging mid-type would be worse than
  // the mistake.
  const hint =
    !focused && !!national && !draft.possible
      ? `That doesn't look like a full phone number for ${countryNameFor(dialCode)}.`
      : (error ?? null);

  return (
    <div className={styles.PhoneStack}>
      {/* Country and number are one field to the customer, so they share a
          row. The hint stays outside it to span the full width underneath. */}
      <div className={styles.PhoneRow}>
        <button
          className={styles.DialCodeBtn}
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={disabled}
        >
          <span className={styles.DialFlag}>{dialCode.flag}</span>
          <span className={styles.DialCode}>{dialCode.dial_code}</span>
          <ChevronDown size={14} className={styles.DialChevron} />
        </button>

        <input
          className={styles.Input}
          type="tel"
          autoComplete="tel-national"
          placeholder="Phone number"
          value={focused ? national : formatNational(national, dialCode.code)}
          onChange={(e) => handleLocalChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
        />
      </div>

      {hint && <span className={styles.Hint}>{hint}</span>}

      <DialCodePickerModal
        visible={pickerOpen}
        setVisible={setPickerOpen}
        select={handleDialCodeChange}
      />
    </div>
  );
};

export default PhoneInput;
